import { Reducer, Action } from 'redux';
import { LOCATION_CHANGE, RouterState } from 'connected-react-router';
import LoginResponse from 'src/models/LoginResponse';
import { decode } from 'jsonwebtoken';
import Authorization from 'src/util/Authorization';
import { actionTypes as authActionTypes } from 'src/reducers/Auth';
import Profile from 'src/models/Profile';
import { UserRoles } from 'src/models/UserRole';

export interface IAppContextState {
    currentUser: Profile;
    projectNumber: number;
    clientNumber: number;
    mapNumber: number;
    sectionNumber: number;
    fieldNumber: number;
    tractNumber: number;
    surfaceTractNumber: number;
    horizonNumber: number;
    ownershipId: number;
    leaseholdId: number;
    rowholdId: number;
    surfaceWaiverHoldId: number;
    solarLeaseHoldId: number;
    batteryStorageHoldId: number;
    brineLeaseHoldId: number;
    leaseFormId: number;
    solarLeaseFormId: number;
    batteryStorageFormId: number;
    surfaceWaiverFormId: number;
    leaseId: number;
    rowId: number;
    surfaceWaiverId: number;
    solarLeaseId: number;
    batteryStorageId: number;
    brineLeaseId: number;
    ownerId: number;
    reportId: number;
}

const initialState: IAppContextState = {
    currentUser: undefined,
    projectNumber: undefined,
    clientNumber: undefined,
    sectionNumber: undefined,
    mapNumber: undefined,
    fieldNumber: undefined,
    tractNumber: undefined,
    surfaceTractNumber: undefined,
    horizonNumber: undefined,
    ownershipId: undefined,
    leaseholdId: undefined,
    rowholdId: undefined,
    surfaceWaiverHoldId: undefined,
    solarLeaseHoldId: undefined,
    batteryStorageHoldId: undefined,
    brineLeaseHoldId: undefined,
    leaseFormId: undefined,
    batteryStorageFormId: undefined,
    solarLeaseFormId: undefined,
    surfaceWaiverFormId: undefined,
    leaseId: undefined,
    rowId: undefined,
    surfaceWaiverId: undefined,
    solarLeaseId: undefined,
    batteryStorageId: undefined,
    brineLeaseId: undefined,
    ownerId: undefined,
    reportId: undefined,
};

const getValueForKey = (paths: string[], key: string) => {
    const idx = paths.indexOf(key.toLowerCase());

    if (idx >= 0 && idx + 2 <= paths.length) {
        return parseInt(paths[idx + 1], 10);
    }
    return undefined;
};

const reducer: Reducer<IAppContextState> = (state: IAppContextState = initialState, action: Action): IAppContextState => {
    if (action.type === authActionTypes.LOGIN_SUCCESS) {
        const payload = (action as { type: string; payload: LoginResponse }).payload;
        const decoded = decode(payload.jwt);
        /* tslint:disable:no-string-literal */
        const userState: Profile = {
            ...state.currentUser,
            jwt: payload.jwt,
            username: decoded ? decoded['un'] : '',
            email: decoded ? decoded['em'] : '',
            userId: decoded ? decoded['id'] : 0,
            loggedIn: true,
            role: decoded ? Authorization.JwtUserRole(decoded['rol']) : undefined,
            type: decoded ? Authorization.JwtUserType(decoded['typ']) : undefined,
            twoFactorEnabled: decoded && decoded['tfe'],
        };

        if (userState.role === UserRoles.Admin) {
            userState.isAdmin = true;
        }

        return { ...state, currentUser: userState };
    }

    if (action.type === LOCATION_CHANGE) {
        const paths = (action as { type: string; payload: RouterState }).payload.location.pathname.split('/').map((s) => s.toLowerCase());

        const user = state.currentUser;

        const newState = { ...initialState };

        // TODO: LeaseFormId's and leaseId's are the same in any context. Verify the leaseForm props can be deleted.
        newState.clientNumber = getValueForKey(paths, 'clients');
        newState.projectNumber = getValueForKey(paths, 'projects');
        newState.mapNumber = getValueForKey(paths, 'maps');
        newState.sectionNumber = getValueForKey(paths, 'sections');
        newState.fieldNumber = newState.mapNumber ?? newState.sectionNumber;
        newState.tractNumber = getValueForKey(paths, 'tracts');
        newState.surfaceTractNumber = getValueForKey(paths, 'surface-tracts');
        newState.rowId = getValueForKey(paths, 'row');
        newState.rowholdId = getValueForKey(paths, 'rowhold');
        newState.surfaceWaiverId = getValueForKey(paths, 'surface-waivers');
        newState.surfaceWaiverHoldId = getValueForKey(paths, 'surface-waiver-holds');
        newState.surfaceWaiverFormId = getValueForKey(paths, 'surface-waivers');
        newState.solarLeaseId = getValueForKey(paths, 'solar-leases');
        newState.solarLeaseFormId = getValueForKey(paths, 'solar-leases');
        newState.solarLeaseHoldId = getValueForKey(paths, 'solar-lease-holds');
        newState.batteryStorageId = getValueForKey(paths, 'battery-storage');
        newState.batteryStorageHoldId = getValueForKey(paths, 'battery-storage-holds');
        newState.batteryStorageFormId = getValueForKey(paths, 'battery-storage');
        newState.brineLeaseId = getValueForKey(paths, 'brine-leases');
        newState.brineLeaseHoldId = getValueForKey(paths, 'brine-lease-holds');
        newState.horizonNumber = getValueForKey(paths, 'horizons');
        newState.ownershipId = getValueForKey(paths, 'ownership');
        newState.leaseholdId = getValueForKey(paths, 'leaseholds');
        newState.leaseFormId = getValueForKey(paths, 'leaseFormId');
        newState.leaseId = getValueForKey(paths, 'leases');
        newState.ownerId = getValueForKey(paths, 'ownerId');
        newState.reportId = getValueForKey(paths, 'reports');
        newState.currentUser = user;

        return newState;
    }

    return state;
};

export default reducer;



// WEBPACK FOOTER //
// ./src/reducers/AppContext.ts